import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  registerPhoneWebhook,
  refreshWhatsappTokenDebug,
  syncAllPhoneHealth,
  refreshProfilePictures,
} from "../apis/whatsappAccount";
import api from "../services/api";
import ManualConnectModal from "../components/whatsapp/ManualConnectModal";
import ConnectMethodPicker from "../components/whatsapp/ConnectMethodPicker";
import ConnectResultBanner from "../components/whatsapp/ConnectResultBanner";
import WhatsappConnectOverlay from "../components/whatsapp/WhatsappConnectOverlay";
import ConnectionTypeBadge from "../components/whatsapp/ConnectionTypeBadge";
import PhoneProfileAvatar from "../components/whatsapp/PhoneProfileAvatar";
import {
  getEmbeddedSignupLoginOptions,
  buildEmbeddedSignupCallbackBodyAsync,
  installEmbeddedSignupMessageListener,
  clearEmbeddedSignupSession,
} from "../config/facebookEmbeddedSignup";
import {
  formatMessagingLimit,
  formatQualityRating,
  formatNameStatus,
  qualityTone,
  nameStatusTone,
} from "../utils/phoneHealthLabels";

const toneDot = {
  good: "bg-emerald-500",
  warn: "bg-amber-500",
  bad: "bg-red-500",
  neutral: "bg-gray-400",
};

const toneBadge = {
  good: "bg-emerald-100 text-emerald-800 border-emerald-200",
  warn: "bg-amber-100 text-amber-900 border-amber-200",
  bad: "bg-red-100 text-red-800 border-red-200",
  neutral: "bg-gray-100 text-gray-700 border-gray-200",
};

const WhatsappBusiness = () => {
  const navigate = useNavigate();
  const [fetching, setFetching] = useState(false);
  const [overlayType, setOverlayType] = useState(null);
  const [connected, setConnected] = useState(false);
  const [pageError, setPageError] = useState("");
  const [connectResult, setConnectResult] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [reVerifyingPhoneId, setReVerifyingPhoneId] = useState(null);
  const [connectPickerOpen, setConnectPickerOpen] = useState(false);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [editPhoneId, setEditPhoneId] = useState(null);
  const [refreshingPhoneId, setRefreshingPhoneId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);

  const fetchWhatsappAccounts = async () => {
    try {
      setFetching(true);
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await api.get("/api/whatsapp-accounts", {
        params: { section: "phone_numbers" },
      });

      const data = res.data;
      if (data.success) {
        let list = data.data || [];
        setAccounts(list);
        if (list.length > 0) setConnected(true);

        // Backfill missing DPs from Meta (async — update cards when done)
        const missing = list.filter((a) => !a.profile_picture_url);
        if (missing.length > 0) {
          refreshProfilePictures()
            .then((picRes) => {
              const pictures = picRes.data?.data?.pictures || [];
              if (!pictures.length) return;
              const byId = Object.fromEntries(
                pictures.map((p) => [String(p.phone_id), p.profile_picture_url])
              );
              setAccounts((prev) =>
                prev.map((a) =>
                  byId[String(a.phone_id)]
                    ? { ...a, profile_picture_url: byId[String(a.phone_id)] }
                    : a
                )
              );
            })
            .catch(() => {});
        }
      } else {
        setPageError(data.error || "Failed to load WhatsApp accounts");
      }
    } catch (err) {
      setPageError("Server error while fetching WhatsApp accounts");
    } finally {
      setFetching(false);
    }
  };

  const handleSyncAllHealth = async () => {
    try {
      setSyncingAll(true);
      setPageError("");
      const res = await syncAllPhoneHealth();
      if (res.data?.success) {
        setConnectResult({
          type: "success",
          title: "Health synced",
          message: "Quality, messaging limits, and webhook subscriptions updated for your numbers.",
        });
        await fetchWhatsappAccounts();
      } else {
        setPageError(res.data?.error || "Failed to sync phone health");
      }
    } catch (err) {
      setPageError(err.response?.data?.error || "Failed to sync phone health");
    } finally {
      setSyncingAll(false);
    }
  };

  // Re-verify phone number and register webhook
  const handleReVerifyPhone = async (phoneId) => {
    try {
      setReVerifyingPhoneId(phoneId);
      const token = localStorage.getItem("token");
      if (!token) {
        setConnectResult({
          type: "error",
          title: "Authentication required",
          message: "Please log in again to continue.",
        });
        return;
      }

      const response = await registerPhoneWebhook(phoneId);
      if (response.data.success) {
        setConnectResult({
          type: "success",
          title: "Re-verification complete",
          message: "Phone number re-verified and webhook registered successfully.",
        });
        fetchWhatsappAccounts();
      } else {
        setPageError(response.data.message || "Failed to re-verify phone number.");
      }
    } catch (err) {
      setPageError(
        err.response?.data?.error ||
          "Server error while re-verifying phone number. Please try again."
      );
    } finally {
      setReVerifyingPhoneId(null);
    }
  };

  // Delete WhatsApp Phone Number
  const getDisconnectConfirmMessage = (account) => {
    const isManual = account?.connection_type === "manual";
    if (isManual) {
      return [
        "Disconnect this manually connected number?",
        "",
        "• Phone will be deregistered on Meta (undoes Cloud API register)",
        "• WABA subscription on Meta will NOT be changed",
        "• Credentials will be removed from Blutec Ping",
      ].join("\n");
    }
    return [
      "Disconnect this Facebook-connected number?",
      "",
      "• Phone will be deregistered on Meta",
      "• If this is your last number on the WABA, the app will unsubscribe from it",
      "• Credentials will be removed from Blutec Ping",
    ].join("\n");
  };

  const handleDeletePhoneNumber = async (phone_id) => {
    const account = accounts.find((a) => String(a.phone_id) === String(phone_id));
    if (!window.confirm(getDisconnectConfirmMessage(account))) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      setOverlayType("disconnecting");

      const response = await api.delete(`/api/whatsapp-accounts/${phone_id}`);

      const data = response.data;

      if (data.success) {
        await fetchWhatsappAccounts();
        window.dispatchEvent(new CustomEvent("phoneAccountsUpdated"));
        setConnectResult({
          type: data.partial ? "partial" : "success",
          title: data.partial ? "Disconnected with warnings" : "Disconnected",
          message: data.message || "WhatsApp number disconnected successfully.",
          setupSteps: data.steps || [],
        });
      } else {
        setConnectResult({
          type: "error",
          title: "Disconnect failed",
          message: data.message || "Something went wrong.",
        });
      }
    } catch (err) {
      setConnectResult({
        type: "error",
        title: "Disconnect failed",
        message: "Server error while disconnecting phone number.",
      });
    } finally {
      setOverlayType(null);
    }
  };

  // Send code to backend
  const sendCodeToBackend = async (code) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setConnectResult({
        type: "error",
        title: "Authentication required",
        message: "Please log in again to continue.",
      });
      return;
    }

    try {
      setOverlayType("adding");
      setConnectResult(null);

      const body = await buildEmbeddedSignupCallbackBodyAsync(code);
      if (body.cancelled) {
        setOverlayType(null);
        setConnectResult({
          type: "error",
          title: "Signup cancelled",
          message: "WhatsApp signup was cancelled.",
        });
        clearEmbeddedSignupSession();
        return;
      }

      const res = await api.post("/auth/facebook/embedded-signup/callback", body);
      clearEmbeddedSignupSession();
      const data = res.data;

      await fetchWhatsappAccounts();
      window.dispatchEvent(new CustomEvent("phoneAccountsUpdated"));
      setOverlayType(null);

      if (data.success) {
        const processedCount = data.processed?.length || 0;
        if (data.partial) {
          setConnectResult({
            type: "partial",
            title: `${processedCount} number(s) connected (partial)`,
            message: data.message || "Some setup steps had warnings.",
            setupSteps: buildEmbeddedSetupSteps(data),
          });
        } else {
          setConnectResult({
            type: "success",
            title: "Connected via Facebook",
            message: `${processedCount} WhatsApp number(s) added and activated successfully.`,
            setupSteps: buildEmbeddedSetupSteps(data),
          });
        }
      } else {
        setConnectResult({
          type: "error",
          title: "Embedded signup failed",
          message: data.error || data.message || "Failed to complete onboarding.",
        });
      }
    } catch (error) {
      const metaMsg = error.response?.data?.details?.error?.message;
      const details =
        typeof error.response?.data?.details === "string"
          ? error.response.data.details
          : metaMsg;
      setOverlayType(null);
      setConnectResult({
        type: "error",
        title: "Embedded signup failed",
        message:
          error.response?.data?.message ||
          details ||
          error.response?.data?.error ||
          "Failed to complete onboarding. Please try again.",
      });
      clearEmbeddedSignupSession();
    }
  };

  function buildEmbeddedSetupSteps(data) {
    const steps = [];
    if (data.processed?.length) {
      data.processed.forEach((p) => {
        steps.push({
          step: "register_phone",
          status: "success",
          label: `Phone registered: ${p.number || p.phone_id}`,
          message: p.webhook_status,
        });
      });
    }
    (data.webhookFailures || []).forEach((f) => {
      steps.push({
        step: "register_phone",
        status: "failed",
        label: `Webhook issue: ${f.number || f.phone_id}`,
        message: f.message || f.error,
      });
    });
    if (data.warnings?.length) {
      data.warnings.forEach((w) => {
        steps.push({ step: "scope", status: "failed", label: "Scope warning", message: w });
      });
    }
    return steps;
  };

  const handleEmbeddedSignup = () => {
    setConnectResult(null);
    setPageError("");
    clearEmbeddedSignupSession();

    const token = localStorage.getItem("token");
    if (!token) {
      setConnectResult({
        type: "error",
        title: "Authentication required",
        message: "Please log in to your account first.",
      });
      return;
    }

    if (window.FB && typeof window.FB.login === "function") {
      window.FB.login(
        (response) => {
          if (response.authResponse?.code) {
            sendCodeToBackend(response.authResponse.code);
          } else {
            setConnectResult({
              type: "error",
              title: "Facebook signup failed",
              message: "Failed to get authorization code. Please try again.",
            });
          }
        },
        getEmbeddedSignupLoginOptions()
      );
    } else {
      setConnectResult({
        type: "error",
        title: "Facebook SDK unavailable",
        message: "Facebook SDK not loaded. Please refresh and try again.",
      });
    }
  };

  const handleManualConnectStart = () => {
    setOverlayType("adding-manual");
    setConnectResult(null);
  };

  const handleManualConnectEnd = () => {
    setOverlayType(null);
  };

  const handleRefreshToken = async (phoneId) => {
    try {
      setRefreshingPhoneId(phoneId);
      setConnectResult(null);
      const res = await refreshWhatsappTokenDebug(phoneId, { syncToDb: true });
      const data = res.data;
      if (data.success) {
        await fetchWhatsappAccounts();
        setConnectResult({
          type: data.data?.token?.missing_required_scopes?.length ? "partial" : "success",
          title: "Token refreshed",
          message: data.message || "Token verified and account details synced from Meta.",
          tokenDebug: data.data?.token || null,
        });
      } else {
        setConnectResult({
          type: "error",
          title: "Token refresh failed",
          message: data.error || "Could not debug access token.",
        });
      }
    } catch (err) {
      setConnectResult({
        type: "error",
        title: "Token refresh failed",
        message: err.response?.data?.error || "Server error while debugging token.",
      });
    } finally {
      setRefreshingPhoneId(null);
    }
  };

  const handleManualConnectSuccess = async (data) => {
    await fetchWhatsappAccounts();
    window.dispatchEvent(new CustomEvent("phoneAccountsUpdated"));

    const baseResult = {
      setupSteps: data?.setupSteps || [],
      webhookNote: data?.webhookNote || null,
      tokenDebug: data?.token || data?.data?.token || null,
    };

    if (data?.partial) {
      setConnectResult({
        ...baseResult,
        type: "partial",
        title: "Number connected (partial)",
        message: data.message || "Phone registered for messaging but some Meta API steps had warnings.",
      });
    } else {
      setConnectResult({
        ...baseResult,
        type: "success",
        title: editPhoneId ? "Credentials updated" : "Number activated",
        message:
          data?.message ||
          (editPhoneId
            ? "WhatsApp credentials updated successfully."
            : "Phone registered for Cloud API and saved. See steps below for what Meta APIs were called."),
      });
    }
  };

  const handleManualConnectError = (result) => {
    setConnectResult(result);
  };

  const openConnectPicker = () => {
    setConnectResult(null);
    setConnectPickerOpen(true);
  };

  const openManualConnect = (phoneId = null) => {
    setEditPhoneId(phoneId);
    setManualModalOpen(true);
  };

  const closeManualConnect = () => {
    setManualModalOpen(false);
    setEditPhoneId(null);
  };

  const handleRetry = () => {
    setPageError("");
    setConnectResult(null);
    fetchWhatsappAccounts();
  };

  // Check if phone has verification or webhook issues
  const hasVerificationIssue = (account) => {
    return (
      account.code_verification_status === "EXPIRED" ||
      account.is_webhook_registered === 0
    );
  };

  // Get appropriate error message
  const getErrorMessage = (account) => {
    if (account.code_verification_status === "EXPIRED") {
      return "Verification code has expired";
    }
    if (account.is_webhook_registered === 0) {
      return `Webhook registration failed: ${
        account.webhook_registration_error || "Unknown error"
      }`;
    }
    return "";
  };

  useEffect(() => {
    fetchWhatsappAccounts();
    return installEmbeddedSignupMessageListener();
  }, []);

  return (
    <div className="min-h-screen bg-[#ffff] p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                WhatsApp Business
              </h1>
              <p className="text-sm text-gray-600">
                Manage your WhatsApp Business accounts
              </p>
            </div>
            {connected && accounts.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSyncAllHealth}
                  disabled={Boolean(overlayType) || fetching || syncingAll}
                  className="inline-flex items-center gap-2 text-gray-800 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    className={`w-4 h-4 ${syncingAll ? "animate-spin" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {syncingAll ? "Syncing…" : "Sync Health"}
                </button>
                <button
                  onClick={openConnectPicker}
                  disabled={Boolean(overlayType) || fetching}
                  className="inline-flex items-center gap-2 text-gray-900 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
                  style={{ backgroundColor: "#eefa94" }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Number
                </button>
              </div>
            )}
          </div>

          {/* Stats Cards */}
          {connected && accounts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div
                className="rounded-2xl p-5 border border-gray-200 hover:shadow-sm transition-all"
                style={{ backgroundColor: "#e9eeea" }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-2 font-medium uppercase tracking-wide">
                      Total Accounts
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {new Set(accounts.map((a) => a.waba_id || a.id)).size}
                    </p>
                  </div>
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: "#eefa94" }}
                  >
                    <svg
                      className="w-7 h-7 text-gray-900"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div
                className="rounded-2xl p-5 border border-gray-200 hover:shadow-sm transition-all"
                style={{ backgroundColor: "#eefa94" }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-800 mb-2 font-medium uppercase tracking-wide">
                      Phone Numbers
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {accounts.length}
                    </p>
                  </div>
                  <div className="w-14 h-14 bg-white/60 rounded-2xl flex items-center justify-center border border-gray-200">
                    <svg
                      className="w-7 h-7 text-gray-900"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div
                className="rounded-2xl p-5 border border-gray-200 hover:shadow-sm transition-all"
                style={{ backgroundColor: "#e9eeea" }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-2 font-medium uppercase tracking-wide">
                      Active Numbers
                    </p>
                    <p className="text-3xl font-bold text-emerald-600">
                      {accounts.filter((a) => !hasVerificationIssue(a)).length}
                    </p>
                  </div>
                  <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center border border-emerald-200">
                    <svg
                      className="w-7 h-7 text-emerald-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <ConnectResultBanner
          result={connectResult}
          onDismiss={() => setConnectResult(null)}
        />

        {pageError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-800 font-medium">{pageError}</p>
                <button onClick={handleRetry} className="mt-2 text-sm text-red-700 hover:text-red-900 font-medium underline">
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Initial load */}
        {fetching && accounts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="animate-spin h-16 w-16 border-4 border-gray-300 border-t-gray-900 rounded-full"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <svg
                  className="w-6 h-6 text-gray-900"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
            </div>
            <p className="mt-4 text-gray-700 font-medium">Loading accounts...</p>
          </div>
        )}

        {/* Empty State */}
        {!connected && !fetching && !pageError && accounts.length === 0 && (
          <div className="max-w-2xl mx-auto">
            <div
              className="rounded-3xl border border-gray-200 p-10 md:p-12 text-center"
              style={{ backgroundColor: "#e9eeea" }}
            >
              <div
                className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6 border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                <svg className="w-10 h-10 text-gray-900" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Connect Your WhatsApp Business
              </h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Use Facebook for guided setup, or enter API credentials manually — both paths register your number and configure webhooks automatically.
              </p>
              <button
                onClick={openConnectPicker}
                disabled={Boolean(overlayType)}
                className="inline-flex items-center justify-center gap-3 text-gray-900 px-8 py-3.5 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                Get Started
              </button>
            </div>
          </div>
        )}

        {connected && accounts.length > 0 && !fetching && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {accounts.map((account) => {
              const qTone = qualityTone(account.quality_rating);
              const nameSt =
                account.new_name_status &&
                account.new_name_status !== "NONE" &&
                account.new_name_status !== "APPROVED"
                  ? account.new_name_status
                  : account.name_status;
              const nTone = nameStatusTone(nameSt);

              return (
              <div
                key={account.phone_id || account.id}
                className="rounded-2xl border border-gray-200 hover:shadow-md transition-all duration-200 overflow-hidden group bg-white"
              >
                {/* Card Header */}
                <div className="px-5 py-4 border-b border-gray-100" style={{ backgroundColor: "#e9eeea" }}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <PhoneProfileAvatar
                        phoneId={account.phone_id}
                        profilePictureUrl={account.profile_picture_url}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-gray-900 truncate mb-1">
                          {account.phone_name ||
                            account.verified_name ||
                            "Unnamed Account"}
                        </h3>
                        <p className="text-sm text-gray-600 truncate">
                          {account.phone_number}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-2 flex flex-col items-end gap-1.5">
                      <ConnectionTypeBadge connectionType={account.connection_type} />
                      {hasVerificationIssue(account) ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                          <span className="w-1.5 h-1.5 bg-amber-600 rounded-full"></span>
                          Needs attention
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300">
                          <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></span>
                          Active
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Health strip */}
                <div className="px-5 py-3 grid grid-cols-3 gap-2 border-b border-gray-100">
                  <div className="rounded-xl bg-gray-50 border border-gray-100 px-2.5 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                      Quality
                    </p>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${toneDot[qTone]}`} />
                      <span className="text-xs font-bold text-gray-900">
                        {formatQualityRating(account.quality_rating)}
                      </span>
                    </div>
                  </div>
                  <div className="rounded-xl bg-gray-50 border border-gray-100 px-2.5 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                      Limit
                    </p>
                    <span className="text-xs font-bold text-gray-900">
                      {formatMessagingLimit(account.messaging_limit)}
                    </span>
                  </div>
                  <div className="rounded-xl bg-gray-50 border border-gray-100 px-2.5 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                      Name
                    </p>
                    <span
                      className={`inline-flex text-[10px] font-semibold px-1.5 py-0.5 rounded-md border ${toneBadge[nTone]}`}
                    >
                      {formatNameStatus(nameSt)}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5 space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">
                      WABA
                    </label>
                    <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100">
                      <span className="text-xs font-mono text-gray-700 break-all font-medium">
                        {account.waba_name || account.waba_id || "N/A"}
                      </span>
                    </div>
                  </div>

                  {hasVerificationIssue(account) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <p className="text-xs text-amber-800 font-medium mb-2">
                        {getErrorMessage(account)}
                      </p>
                      <button
                        onClick={() => handleReVerifyPhone(account.phone_id)}
                        disabled={reVerifyingPhoneId === account.phone_id}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-2 rounded-xl transition-colors disabled:opacity-50"
                      >
                        {reVerifyingPhoneId === account.phone_id
                          ? "Processing..."
                          : "Re-verify Number"}
                      </button>
                    </div>
                  )}

                  <button
                    onClick={() => navigate(`/waba/${account.phone_id}`)}
                    className="w-full text-gray-900 text-sm font-semibold px-4 py-2.5 rounded-xl transition-all border border-gray-300 flex items-center justify-center gap-2"
                    style={{ backgroundColor: "#eefa94" }}
                  >
                    Manage Profile & Health
                  </button>

                  {account.connection_type === "manual" && (
                  <>
                  <button
                    onClick={() => handleRefreshToken(account.phone_id)}
                    disabled={refreshingPhoneId === account.phone_id}
                    className="w-full bg-white hover:bg-gray-50 text-gray-800 text-sm font-semibold px-4 py-2.5 rounded-xl transition-all border border-gray-200 hover:border-gray-300 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <svg className={`w-4 h-4 ${refreshingPhoneId === account.phone_id ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {refreshingPhoneId === account.phone_id ? "Refreshing…" : "Refresh Token & Sync"}
                  </button>
                  <button
                    onClick={() => openManualConnect(account.phone_id)}
                    className="w-full bg-white hover:bg-gray-50 text-gray-800 text-sm font-semibold px-4 py-2.5 rounded-xl transition-all border border-gray-200 hover:border-gray-300 flex items-center justify-center gap-2"
                  >
                    Update Access Token
                  </button>
                  </>
                  )}

                  <button
                    onClick={() => handleDeletePhoneNumber(account.phone_id)}
                    className="w-full bg-red-50 hover:bg-red-100 text-red-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-all border border-red-200 hover:border-red-300 flex items-center justify-center gap-2"
                  >
                    Disconnect Number
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <ConnectMethodPicker
        open={connectPickerOpen}
        onClose={() => setConnectPickerOpen(false)}
        onSelectFacebook={handleEmbeddedSignup}
        onSelectManual={() => openManualConnect()}
      />

      <ManualConnectModal
        open={manualModalOpen}
        onClose={closeManualConnect}
        onConnectStart={handleManualConnectStart}
        onConnectEnd={handleManualConnectEnd}
        onConnectError={handleManualConnectError}
        onSuccess={handleManualConnectSuccess}
        editPhoneId={editPhoneId}
      />

      <WhatsappConnectOverlay type={overlayType} />
    </div>
  );
};

export default WhatsappBusiness;
